import React from 'react';
import type {
  NeisLearningContentUploadRow,
  NeisLearningContentUploadViewMode,
  NeisUploadLearningContentRow,
} from '../../utils/neisLearningContentUploadRows';

export type NeisLearningContentUploadTableProps = {
  rows: NeisLearningContentUploadRow[] | NeisUploadLearningContentRow[];
  showDateColumns?: boolean;
  variant?: NeisLearningContentUploadViewMode;
  /** 기본값 `순`. xls·pdf export는 `순서` */
  seqColumnLabel?: string;
};

function rowSeq(row: NeisLearningContentUploadRow | NeisUploadLearningContentRow, index: number, variant: NeisLearningContentUploadViewMode): number {
  if (variant === 'neis-upload' && 'trackSeq' in row && Number.isFinite(row.trackSeq)) {
    return row.trackSeq;
  }
  return index + 1;
}

const NeisLearningContentUploadTable: React.FC<NeisLearningContentUploadTableProps> = ({
  rows,
  showDateColumns = false,
  variant = 'annual-plan',
  seqColumnLabel = '순',
}) => {
  const learningColumnLabel = variant === 'neis-upload' ? '학습내용' : '프로그램 내용';
  const withDate = variant === 'annual-plan' && showDateColumns;

  return (
    <table
      className={
        'cp-neis-learning-upload-table' + (withDate ? ' cp-neis-learning-upload-table--with-date' : '')
      }
    >
      <thead>
        <tr>
          <th className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-seq">{seqColumnLabel}</th>
          {withDate ? (
            <>
              <th className="cp-neis-learning-upload-col-center">날짜</th>
              <th className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-narrow">요일</th>
              <th className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-narrow">교시</th>
            </>
          ) : null}
          <th className="cp-neis-learning-upload-col-center">학년</th>
          <th className="cp-neis-learning-upload-col-center">학기</th>
          <th className="cp-neis-learning-upload-col-center">편제</th>
          <th>단원</th>
          <th>{learningColumnLabel}</th>
          <th>쪽수</th>
          <th>보조쪽수</th>
          <th className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-period cp-neis-learning-upload-th-stacked">
            <span className="cp-neis-learning-upload-th-line">해당</span>
            <span className="cp-neis-learning-upload-th-line">차시</span>
          </th>
          <th className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-period cp-neis-learning-upload-th-stacked">
            <span className="cp-neis-learning-upload-th-line">전체</span>
            <span className="cp-neis-learning-upload-th-line">차시</span>
          </th>
          <th>준비물</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={`${row.templateLabel}|${row.dateIso}|${row.weekdayKey}|${row.slotIndex}|${index}`}>
            <td className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-seq">
              {rowSeq(row, index, variant)}
            </td>
            {withDate ? (
              <>
                <td className="cp-neis-learning-upload-col-center">{row.dateLabel}</td>
                <td className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-narrow">
                  {row.weekdayLabel}
                </td>
                <td className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-narrow">
                  {row.periodLabel}
                </td>
              </>
            ) : null}
            <td className="cp-neis-learning-upload-col-center">{row.gradeLabel}</td>
            <td className="cp-neis-learning-upload-col-center">{row.semesterLabel}</td>
            <td className="cp-neis-learning-upload-col-center">{row.templateLabel}</td>
            <td>{row.unit}</td>
            <td className="cp-neis-learning-upload-learning">{row.learning}</td>
            <td>{row.page}</td>
            <td>{row.assistantPage}</td>
            <td className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-period">
              <span className="cp-neis-learning-upload-period-value">{row.currentPeriod}</span>
            </td>
            <td className="cp-neis-learning-upload-col-center cp-neis-learning-upload-col-period">
              <span className="cp-neis-learning-upload-period-value">{row.totalPeriod}</span>
            </td>
            <td>{row.material}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default NeisLearningContentUploadTable;
